import {
  IonButtons,
  IonIcon,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { searchOutline } from 'ionicons/icons';
import { cloneDeep, orderBy } from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHasPermission } from '../../../api/authorization.provider';
import {
  Machine,
  PlanEntitlementKey,
  ScratchpadPlaylistItem,
} from '../../../api/graphql/generated';
import { trackPlaylistShowSearch } from '../../../config/analytics';
import { IntercomArticle } from '../../../config/intercom';
import i18n from '../../../i18n';
import { useIsMachineActive } from '../../../pages/machine/machine-context-provider';
import { DraggableList } from '../../drag/draggable-list';
import { LoadingRetry } from '../../graphql/LoadingRetry';
import { IntercomHelp } from '../../help/intercom-help';
import { PaywallActionButton } from '../../paywall/paywall-action-button';
import { PaywallIonButton } from '../../paywall/paywall-ion-button';
import { isProgramStopped } from '../machine.utils';
import { ScratchpadPlaylistEdit } from './scratchpad-playlist-edit';
import { ScratchpadPlaylistGettingStarted } from './scratchpad-playlist-getting-started';
import {
  ScratchpadPlaylistItemComponent,
  getAutoplayIcon,
} from './scratchpad-playlist-item';
import { useScratchpadPlaylistSearchPopover } from './scratchpad-playlist-search';
import {
  useScratchpadPlaylistCanRecallPlaylist,
  useScratchpadPlaylistContext,
  useScratchpadPlaylistHelpers,
} from './scratchpad-playlist.hooks';

interface ScratchpadPlaylistProps {
  machine?: Machine;
  teamId: string;
}

const ScratchpadPlaylistComponent = (
  props: ScratchpadPlaylistProps
): ReactElement => {
  const { machine, teamId } = props;

  const canUpdate = useHasPermission('update', 'scratchpad_playlist', {
    machineId: machine?.id,
  });
  const canReorderItems = useHasPermission(
    'update',
    'scratchpad_playlist',
    {
      machineId: machine?.id,
    },
    'reorder'
  );
  const canLoadPlaylist = useHasPermission(
    'update',
    'scratchpad_playlist',
    {
      machineId: machine?.id,
    },
    'activated'
  );
  const isMachineActive = useIsMachineActive();

  const programState = machine?.state?.transient?.status?.programState;
  const programStopped = isProgramStopped(programState);

  const {
    playlist: activatedPlaylist,
    statuses,
    data: { error, loading },
  } = useScratchpadPlaylistContext();

  const activatedPlaylistItems = useMemo(() => {
    return activatedPlaylist?.items ?? [];
  }, [activatedPlaylist]);

  const hasActivatedPlaylist = activatedPlaylist?.id ? true : false;
  const hasPlaylistItems = activatedPlaylistItems.length > 0;

  const { reorderActivatedPlaylist } = useScratchpadPlaylistHelpers({
    machineId: machine?.id,
  });

  const [orderedPlaylistItems, setOrderedPlaylistItems] = useState<
    ScratchpadPlaylistItem[]
  >([]);

  useEffect(() => {
    setOrderedPlaylistItems(
      orderBy(activatedPlaylistItems, ['order'], ['asc'])
    );
  }, [activatedPlaylistItems]);

  const showSearch = useScratchpadPlaylistSearchPopover({
    machineId: machine?.id,
  });

  const isDraggable =
    !canUpdate || !canReorderItems || !programStopped || !isMachineActive;

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      let items = cloneDeep(orderedPlaylistItems);
      const from = result.source?.index;
      const to = result.destination?.index;

      const itemToMove = items.splice(from, 1)[0];
      items.splice(to, 0, itemToMove);

      // make sure autoplay flag visually stays at the original index instead of being carried
      // forward on the reordered item.
      // remap the auto play in this section of code so we see the visual change immediately
      // on the UI before persisting the reorder
      const autoPlayToNextValues = orderedPlaylistItems.map(
        (i) => i.autoPlayToNext
      );

      items = items.map((item, index) => {
        let autoPlayToNext = autoPlayToNextValues[index];

        if (index === items.length - 1) {
          autoPlayToNext = false;
        }

        return {
          ...item,
          autoPlayToNext,
        };
      });

      setOrderedPlaylistItems(items);

      await reorderActivatedPlaylist(items);
    },
    [orderedPlaylistItems, reorderActivatedPlaylist]
  );

  const canRecallPlaylist = useScratchpadPlaylistCanRecallPlaylist();

  const playlistTitle = activatedPlaylist?.canRestore
    ? activatedPlaylist?.name
    : `${i18n.t('common:unnamed')}*`;

  return (
    <>
      <IonToolbar className="playlist-toolbar">
        <IonTitle>
          <PaywallActionButton
            planEntitlementKey={PlanEntitlementKey.ENHANCED_PLAYLIST}
            isFeatureAvailable={() => canRecallPlaylist}
            actionButtonProps={{
              buttonProps: {
                fill: 'clear',
                color: 'medium',
              },
              label: playlistTitle,
            }}
          >
            <ScratchpadPlaylistEdit playlist={activatedPlaylist} />
          </PaywallActionButton>
        </IonTitle>
        {canLoadPlaylist && (
          <IonButtons slot="end">
            <PaywallIonButton
              planEntitlementKey={PlanEntitlementKey.ENHANCED_PLAYLIST}
              isFeatureAvailable={() => canRecallPlaylist}
              ionButtonProps={{
                fill: 'clear',
                slot: 'icon-only',
                onClick: () => {
                  trackPlaylistShowSearch();
                  showSearch();
                },
              }}
            >
              <IonIcon icon={searchOutline} slot="icon-only" />
            </PaywallIonButton>
          </IonButtons>
        )}
      </IonToolbar>
      <LoadingRetry
        loading={loading}
        error={error}
        results={{
          hasResults: hasActivatedPlaylist && hasPlaylistItems,
          noResultsComponent: <ScratchpadPlaylistGettingStarted />,
        }}
      >
        <DraggableList
          droppableId="playlistDroppable"
          items={orderedPlaylistItems}
          disabled={isDraggable}
          getItemId={getItemId}
          onDragEnd={onDragEnd}
        >
          {({ item, index }) => {
            const status = statuses?.find((s) => s.playlistIndex === index);

            return (
              <ScratchpadPlaylistItemComponent
                index={index}
                item={item}
                teamId={teamId}
                status={status}
              />
            );
          }}
        </DraggableList>
        <div className="full-height-width content-center">
          <IonText className="ion-padding ion-text-center" color="medium">
            <p className="playlist-smaller-text">
              {i18n.t('common:scratchpad_playlist_add_parts')}
            </p>
            <IntercomHelp
              articleId={IntercomArticle.PLAYLIST_OVERVIEW}
              linkStyle={{ color: 'var(--ion-medium-color)' }}
            >
              <p className="playlist-smaller-text">
                {getAutoplayIcon(true, false)} {getAutoplayIcon(false, false)}{' '}
                {i18n.t(
                  'common:scratchpad_playlist_toggle_autoplay_instructions'
                )}
                <br></br>
                {i18n.t(
                  'common:scratchpad_playlist_toggle_autoplay_learn_more'
                )}
              </p>
            </IntercomHelp>
          </IonText>
        </div>
      </LoadingRetry>
    </>
  );
};

const getItemId = (item: ScratchpadPlaylistItem) => {
  return item.id;
};

export { ScratchpadPlaylistComponent };
